/**
 * Lean Setup Wizard — 3-page wrapper.
 *
 * Single component used by both first-login paths:
 *   - Manual: fields start blank/default.
 *   - Import (Slice 2+): fields prefilled from shop history inferences;
 *     prop `prefillSource='import'` plus initial data passed in.
 *
 * On Finish setup, calls commitWizard() to atomically (best-effort)
 * write shop_profile, technicians, shop_bays, parking layout/spots,
 * and onboarding_state.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

import { ShopBasicsStep } from './wizard/ShopBasicsStep';
import { PeopleCountsStep } from './wizard/PeopleCountsStep';
import { TeamBaysSpecialtyParkingStep } from './wizard/TeamBaysSpecialtyParkingStep';
import { commitWizard } from '@/lib/onboarding/commitWizard';
import type { Page1Data, Page2Data, Page3Data, WizardData } from '@/lib/onboarding/wizardValidation';
import type { InferredSetupField } from '@/lib/onboarding/inferenceLoader';

/** Core-100 R4 (CORE-U, SP-05): which wizard page shows each inferred value. */
const INFERRED_FIELD_PAGE: Record<InferredSetupField['key'], 1 | 2 | 3> = {
  hours: 1,
  technician_roster: 2,
  parking_spot_count: 3,
};

export interface LeanSetupWizardProps {
  prefillSource: 'manual' | 'import';
  initial?: Partial<WizardData>;
  /**
   * Batch 9C: full inferred technician roster (all distinct names
   * from the imported CSV's `Technician Name` column, ordered by
   * job_count descending). Step 3 uses this to top up rows when
   * the user bumps Step 2's tech count past the initially seeded
   * slice — instead of producing blank rows, the wizard pulls the
   * next unused name from this list first. Falls back to blanks
   * once exhausted.
   */
  suggestedTechnicianRoster?: string[];
  /**
   * Batch 9C: distinct advisor names from
   * shop_history_repair_orders.advisor_display_name, ordered by
   * frequency descending. Same top-up contract as the technician
   * roster.
   */
  suggestedAdvisorRoster?: string[];
  /** Core-100 R4 (CORE-U): the import run the prefill came from. */
  importRunId?: string | null;
  /**
   * Core-100 R4 (CORE-U, SP-05): the values prefilled FROM HISTORY. Each is marked as an inference on its page and
   * must be confirmed before Finish; the server refuses an unconfirmed one (INFERENCE_NOT_CONFIRMED).
   */
  inferredFields?: InferredSetupField[];
}

export function LeanSetupWizard({
  prefillSource,
  initial,
  suggestedTechnicianRoster,
  suggestedAdvisorRoster,
  importRunId = null,
  inferredFields = [],
}: LeanSetupWizardProps) {
  const navigate = useNavigate();
  // Batch 9E: refetchUserShops + hard-navigation fallback for the
  // setup-complete handoff. See handleFinish below for the rationale —
  // mirrors the BetaComplete provisioning pattern.
  const { user, activeShop, refetchUserShops } = useAuth();

  const [page, setPage] = useState<1 | 2 | 3>(1);
  const [page1, setPage1] = useState<Page1Data | null>(initial?.page1 ?? null);
  const [page2, setPage2] = useState<Page2Data | null>(initial?.page2 ?? null);
  const [page3, setPage3] = useState<Page3Data | null>(initial?.page3 ?? null);
  const [isCommitting, setIsCommitting] = useState(false);
  const [confirmedFields, setConfirmedFields] = useState<Set<string>>(new Set());
  const fromHistory = prefillSource === 'import' ? inferredFields : [];
  const unconfirmed = fromHistory.filter((f) => !confirmedFields.has(f.key));
  const toggleConfirmed = (key: string, on: boolean) =>
    setConfirmedFields((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });

  const handlePage1Next = (data: Page1Data) => {
    setPage1(data);
    setPage(2);
  };

  const handlePage2Next = (data: Page2Data) => {
    setPage2(data);
    setPage(3);
  };

  const handleFinish = async (data: Page3Data) => {
    if (!user || !activeShop) {
      toast({
        title: 'Cannot finish setup',
        description: 'Active shop not found. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }
    if (!page1 || !page2) {
      toast({
        title: 'Cannot finish setup',
        description: 'Earlier wizard pages are incomplete.',
        variant: 'destructive',
      });
      return;
    }

    if (unconfirmed.length > 0) {
      toast({
        title: 'Confirm what came from your history',
        description: `Please confirm: ${unconfirmed.map((f) => f.label).join(', ')}.`,
        variant: 'destructive',
      });
      return;
    }

    setIsCommitting(true);
    const result = await commitWizard({
      supabase,
      shopId: activeShop.id,
      userId: user.id,
      page1,
      page2,
      page3: data,
      // Browser-only context: commitWizard can be called from tests
      // with no window, so we guard. The edge function prefers its own
      // canonical URL env vars; this is just the fallback hint.
      baseUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
      provenance: fromHistory.length > 0
        ? {
            structure_source: 'import_confirmed',
            import_run_id: importRunId,
            inferred_fields: fromHistory.map((f) => f.key),
            confirmed_fields: Array.from(confirmedFields),
          }
        : { structure_source: 'asserted' },
    });
    setIsCommitting(false);

    if (!result.ok) {
      toast({
        title: 'Setup failed',
        description: result.error ?? 'Something went wrong saving your setup. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    // ONB-1: onboarding was already completed for this user+shop — the
    // commit was a deliberate no-op. Don't claim we created anything;
    // just route to the dashboard.
    if (result.alreadyCompleted) {
      toast({
        title: 'Setup already completed',
        description: 'Your shop is already set up — taking you to your dashboard.',
      });
      await finalizeAndNavigate();
      return;
    }

    setPage3(data);

    // Invite-during-onboarding (Batch 5): if any invite failed, surface
    // a warning toast separately so the operator can retry from Team
    // Management. Successful invites are summarized in the main toast.
    const invites = result.invites ?? [];
    const succeededInvites = invites.filter((i) => i.ok);
    const failedInvites = invites.filter((i) => !i.ok);
    const inviteSummary = succeededInvites.length > 0
      ? ` Sent ${succeededInvites.length} invitation${succeededInvites.length === 1 ? '' : 's'}.`
      : '';
    toast({
      title: 'Setup complete',
      description:
        `Created ${result.technicianIds.length} technician${result.technicianIds.length === 1 ? '' : 's'} ` +
        `and ${result.bayIds.length} bay${result.bayIds.length === 1 ? '' : 's'}.${inviteSummary}`,
    });
    if (failedInvites.length > 0) {
      toast({
        title: `${failedInvites.length} invitation${failedInvites.length === 1 ? '' : 's'} failed`,
        description: `${failedInvites.map((i) => i.email).join(', ')} — retry from Team Management.`,
        variant: 'destructive',
      });
    }

    // ONB-5: surface non-fatal layout-finishing failures instead of
    // letting them be silently swallowed inside commitWizard. The shop
    // is usable (scheduling works); this tells the operator exactly what
    // to fix and where.
    const degraded = result.degraded ?? [];
    if (degraded.length > 0) {
      toast({
        title: 'Some layout details need attention',
        description: degraded.map((d) => d.detail).join(' '),
        variant: 'destructive',
      });
    }

    await finalizeAndNavigate();
  };

  // ONB-4: deterministic post-commit navigation.
  //
  // Pre-K28 this was `navigate('/')` followed by a 500ms `setTimeout`
  // hard-navigation race — the hard nav fired on an arbitrary timer and
  // only "sometimes" landed the user on the dashboard. The real problem
  // is that the wizard route lives OUTSIDE the auth/billing gates and
  // those gates evaluate a sign-in-time snapshot, so an SPA nav to `/`
  // could be bounced before the freshly-committed onboarding_state /
  // memberships were re-read.
  //
  // Deterministic fix: await `refetchUserShops()` so AuthContext re-reads
  // the canonical state, THEN perform a single full-document load to `/`.
  // A full load is the correct terminal state for a once-per-shop action:
  // every gate (ProtectedRoute / BillingGate) re-evaluates against the
  // just-written data, and the wizard / import-run query caches are
  // cleared. No timer, no race. In non-browser/test contexts (no
  // `window.location.assign`) we fall back to the SPA navigation so the
  // flow still resolves deterministically.
  async function finalizeAndNavigate() {
    try {
      await refetchUserShops();
    } catch (refreshErr) {
      console.warn('[LeanSetupWizard] refetchUserShops after commit failed (non-fatal):', refreshErr);
    }
    if (typeof window !== 'undefined' && typeof window.location?.assign === 'function') {
      window.location.assign('/');
    } else {
      navigate('/', { replace: true });
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4" data-testid="lean-setup-wizard">
      <div className="w-full flex flex-col items-center gap-3">
        {/* Progress indicator */}
        <div className="text-sm text-muted-foreground" data-testid="wizard-progress">
          Step {page} of 3 {prefillSource === 'import' ? '· prefilled from your shop history' : ''}
        </div>

        {/* Core-100 R4 (CORE-U, SP-05): each value inferred from history is marked as an inference on its own page and
            must be confirmed before it becomes shop truth. */}
        {fromHistory.filter((f) => INFERRED_FIELD_PAGE[f.key] === page).length > 0 && (
          <div className="w-full max-w-2xl rounded-md border border-sky-500/40 bg-sky-500/10 p-3 text-sm" data-testid="inferred-fields-panel">
            <div className="font-medium">From your shop history — please confirm or correct</div>
            <ul className="mt-2 space-y-1">
              {fromHistory.filter((f) => INFERRED_FIELD_PAGE[f.key] === page).map((f) => (
                <li key={f.key}>
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={confirmedFields.has(f.key)}
                      onChange={(e) => toggleConfirmed(f.key, e.target.checked)}
                      data-testid={`inferred-field-${f.key}`}
                    />
                    <span>
                      <span className="rounded bg-sky-500/20 px-1 text-xs uppercase tracking-wide">Inferred</span>{' '}
                      {f.label} — I have checked it below
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}

        {page === 1 && (
          <ShopBasicsStep
            initial={page1 ?? {}}
            onNext={handlePage1Next}
          />
        )}
        {page === 2 && (
          <PeopleCountsStep
            initial={page2 ?? {}}
            onBack={() => setPage(1)}
            onNext={handlePage2Next}
          />
        )}
        {page === 3 && page2 && (
          <TeamBaysSpecialtyParkingStep
            initial={page3 ?? {}}
            technicianCount={page2.num_technicians}
            advisorCount={page2.num_advisors}
            managerCount={page2.num_managers}
            shopHours={page1?.hours ?? null}
            suggestedTechnicianRoster={suggestedTechnicianRoster}
            suggestedAdvisorRoster={suggestedAdvisorRoster}
            onBack={() => setPage(2)}
            onFinish={handleFinish}
            isCommitting={isCommitting}
            // Batch 9: persist Step 3 draft on every change so round-
            // tripping back to Step 2 and forward doesn't destroy
            // user edits to names / emails / classifications.
            onDraftChange={(draft) => setPage3(draft)}
            // Batch 9: Add/Delete row in Step 3 bumps the Step 2 count
            // for the affected role so Step 2 ↔ Step 3 stay in sync
            // and the row-reconciler on re-mount doesn't undo the
            // user's manual addition/removal.
            onCountDelta={(delta) => {
              setPage2((prev) => {
                if (!prev) return prev;
                return {
                  num_technicians: Math.max(1, prev.num_technicians + (delta.tech ?? 0)),
                  num_advisors: Math.max(0, prev.num_advisors + (delta.advisor ?? 0)),
                  num_managers: Math.max(0, prev.num_managers + (delta.manager ?? 0)),
                };
              });
            }}
          />
        )}
      </div>
    </div>
  );
}
